/**
 * Tenant Storage Widget
 * 
 * Displays storage usage for top tenants with progress bars.
 * Shows up to 5 tenants ordered by storage usage.
 */

import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui';
import { Progress } from '@/components/ui/progress';
import { useSystemTenants } from '../hooks';
import { Database, ArrowRight } from 'lucide-react';

function WidgetSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-40" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-2 w-full" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function TenantStorageWidget() {
  const { data: tenantsResponse, isLoading, error } = useSystemTenants({ limit: 5 });

  if (isLoading) {
    return <WidgetSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Tenant Storage Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Failed to load tenant storage data
          </p>
        </CardContent>
      </Card>
    );
  }

  const tenants = tenantsResponse?.data ?? [];

  // Sort by storage percent descending
  const sortedTenants = [...tenants].sort((a, b) => b.storage_percent - a.storage_percent);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Tenant Storage Usage
        </CardTitle>
        <Link 
          to="/system/tenants" 
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          View all
          <ArrowRight className="h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent>
        {sortedTenants.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No tenants found
          </p>
        ) : (
          <div className="space-y-3">
            {sortedTenants.map((tenant) => {
              const isCritical = tenant.storage_percent > 90;
              const isWarning = tenant.storage_percent > 75;

              return (
                <div key={tenant.id} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium truncate max-w-[60%]">{tenant.name}</span>
                    <span className={isCritical ? 'text-destructive' : isWarning ? 'text-amber-600' : 'text-muted-foreground'}>
                      {tenant.storage_percent.toFixed(0)}%
                    </span>
                  </div>
                  <Progress 
                    value={tenant.storage_percent}
                    className={isCritical ? '[&>div]:bg-destructive' : isWarning ? '[&>div]:bg-amber-500' : ''}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{tenant.storage_used_gb.toFixed(1)} GB used</span>
                    <span>{tenant.storage_quota_gb} GB quota</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
